import React, { useState, useContext, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useCategoriesContext } from "domain/Category";
import { useCompaniesContext } from "domain/Company";
import { InventoryService } from "../../Inventory/services";
import { PAGINATION_DEFAULTS, PAGINATION_QUERY_KEYS } from "shared/constants/constants";

const ExportContext = React.createContext();

export function useExportContext() {
  return useContext(ExportContext);
}

export function ExportProvider({ children }) {
  const { categories } = useCategoriesContext();
  const { companies, temporaryOwners } = useCompaniesContext();
  const allCompanyLikeEntities = [...companies, ...temporaryOwners];

  const [applyExportFilters, setApplyExportFilters] = useState({});
  const [pendingExportFilters, setPendingExportFilters] = useState({});
  const [exportItems, setExportItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedExportItems, setSelectedExportItems] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = useMemo(
    () => parseInt(searchParams.get(PAGINATION_QUERY_KEYS.PAGE) || PAGINATION_DEFAULTS.PAGE, 10),
    [searchParams]
  );
  const pageSize = useMemo(
    () => parseInt(searchParams.get(PAGINATION_QUERY_KEYS.PAGE_SIZE) || PAGINATION_DEFAULTS.PAGE_SIZE, 10),
    [searchParams]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set(PAGINATION_QUERY_KEYS.PAGE, newPage.toString());
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const updateExportSelection = (selectionArray) => {
    setSelectedExportItems(selectionArray);
  };

  const saveExportFilter = useCallback((filter) => {
    setPendingExportFilters((existing) => {
      if (filter.values.length === 0) return existing;
      return { ...existing, [filter.name]: filter.values.join(",") };
    });
  }, []);

  const removeExportFilter = useCallback((filterName) => {
    setPendingExportFilters((existing) => {
      const cleanedFilters = { ...existing };
      delete cleanedFilters[filterName];
      return cleanedFilters;
    });
  }, []);

  const applyPendingExportFilters = useCallback(() => {
    setApplyExportFilters(pendingExportFilters);
    handlePageChange(1);
  }, [handlePageChange, pendingExportFilters]);

  const clearFilters = () => {
    setApplyExportFilters({});
    setPendingExportFilters({});
    setExportItems([]);
    setTotalItems(0);
  };

  useEffect(() => {
    if (!categories.length || !companies.length || !Object.keys(applyExportFilters).length) {
      setExportItems([]);
      setTotalItems(0);
      return;
    }

    const params = { ...applyExportFilters, page: currentPage, pageSize };
    setIsLoading(true);
    setSelectedExportItems([]);

    InventoryService.findPage(params)
      .then((response) => {
        const { data, total } = response;
        setTotalItems(total);
        setExportItems(
          (data || []).map((item) => {
            const { companyId, categoryId, ownerId, ...other } = item;
            const company = companies.find((c) => c.id === companyId);
            const category = categories.find((c) => c.id === categoryId);
            const owner = allCompanyLikeEntities.find((o) => o.id === ownerId);
            return {
              ...other,
              company,
              category,
              owner,
              parts: (item.parts || []).map((part) => ({
                ...part,
                category: categories.find((c) => c.id === part.categoryId),
                company: companies.find((c) => c.id === part.companyId),
                owner: allCompanyLikeEntities.find((o) => o.id === part.ownerId),
              })),
            };
          })
        );
      })
      .finally(() => setIsLoading(false));
  }, [applyExportFilters, searchParams, companies, categories, currentPage, pageSize]);

  return (
    <ExportContext.Provider
      value={{
        applyExportFilters,
        pendingExportFilters,
        saveExportFilter,
        removeExportFilter,
        applyPendingExportFilters,
        clearFilters,
        exportItems,
        totalItems,
        isLoading,
        currentPage,
        pageSize,
        handlePageChange,
        selectedExportItems,
        updateExportSelection,
      }}
    >
      {children}
    </ExportContext.Provider>
  );
}
